import apiReq from 'core/api';

/** Тип процесса для init загрузки перед Docling (общая информация / контекстные документы). */
export const PROCESS_TYPE_CONTEXT_DOCUMENT_PARSING = 'context_document_parsing';

const UPLOAD_CONCURRENCY = 4;

/**
 * Загрузка файла по чанкам: init → part × N → complete.
 * Аналогично SummarizerPage.uploadFileChunked, без глобальной блокировки одной загрузки.
 *
 * processType опционален: для файлов общего назначения (например, file-поля в Bitrix-форме)
 * передавать не нужно — бэк не запускает фоновую обработку.
 */
export async function uploadFileChunked(
  file: File,
  projectId: string | number,
  processType?: string,
  onProgress?: (percent: number) => void
): Promise<string> {
  const initBody: Record<string, unknown> = {
    project_id: projectId,
    name: file.name,
    size: file.size,
    mime_type: file.type || 'application/octet-stream',
  };
  if (processType) {
    initBody.process_type = processType;
  }

  const initData = await apiReq<{
    file_id: string;
    part_size: number;
    parts_total: number;
  }>('file/upload/init', 'POST', initBody);
  if (!initData?.file_id) {
    throw new Error('Не удалось начать загрузку файла');
  }
  const { file_id, part_size, parts_total } = initData;

  const results: { part_number: number; etag: string }[] = new Array(parts_total);
  let completedParts = 0;

  const uploadPart = async (partIndex: number) => {
    const start = partIndex * part_size;
    const end = Math.min(start + part_size, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('file_id', file_id);
    formData.append('part_number', String(partIndex + 1));
    formData.append('chunk', chunk);

    const partData = await apiReq<{ part_number: number; etag: string }>(
      'file/upload/part',
      'POST',
      formData,
      true
    );
    if (!partData) {
      throw new Error('Пустой ответ при загрузке части файла');
    }
    results[partIndex] = { part_number: partData.part_number, etag: partData.etag };

    completedParts++;
    if (onProgress) {
      onProgress(Math.round((completedParts / parts_total) * 100));
    }
  };

  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < parts_total) {
      const idx = nextIndex++;
      await uploadPart(idx);
    }
  };

  const workers = Array.from({ length: Math.min(UPLOAD_CONCURRENCY, parts_total) }, () => worker());
  await Promise.all(workers);

  await apiReq('file/upload/complete', 'POST', { file_id, parts: results });

  return file_id;
}
